import { Component, Input, OnDestroy } from '@angular/core';

import { first, Subject, switchMap, takeUntil } from 'rxjs';

import { ApiChapterSubjectCategoryListItem, ApiReplaceSubjectCategoryRequest, ApiSubjectCategory, ApiTopic, ChaptersClient, SubjectCategoriesClient, SubjectCategoryType } from 'content-manager/src/app/core/open-api/content-manager-api-client';
import { ProjectStateService } from 'content-manager/src/app/projects/project/project-state.service';
import { TableSettings } from 'sc-common';

@Component({
    selector: 'cm-chapter-topics',
    templateUrl: './chapter-topics.component.html',
    standalone: false
})
export class ChapterTopicsComponent implements OnDestroy {

    @Input({ required: true }) public isReadonly = false;

    public readonly tableSettings: TableSettings<ApiChapterSubjectCategoryListItem>;

    public selectedChapter: ApiChapterSubjectCategoryListItem | null = null;

    public selectedTopicHeadings: string[] = [];

    public readonly selectChapterHint = $localize`Select a chapter to edit its topics.`;

    public requestInProcess = false;

    private _originalSelectedHeadings: string[] = [];

    private readonly _refreshTable$ = new Subject<void>();

    private readonly _destroy$ = new Subject<void>();

    constructor(
        private readonly _projectStateService: ProjectStateService,
        private readonly _subjectCategoriesClient: SubjectCategoriesClient,
        private readonly _chaptersClient: ChaptersClient) {

        this.tableSettings = this._initTable();
    }

    public get hasChanges(): boolean {

        if (this._originalSelectedHeadings.length !== this.selectedTopicHeadings.length) {
            return true;
        }

        const sortedOriginal = [...this._originalSelectedHeadings].sort();
        const sortedCurrent = [...this.selectedTopicHeadings].sort();

        return sortedOriginal.some((v, i) => v !== sortedCurrent[i]);
    }

    public get canUpdate(): boolean {
        return !this.isReadonly && !!this.selectedChapter && this.hasChanges && !this.requestInProcess;
    }

    public ngOnDestroy(): void {
        this._destroy$.next();
        this._destroy$.complete();
        this._refreshTable$.complete();
    }

    public onSelectionChange(chapter: ApiChapterSubjectCategoryListItem | null): void {

        this.selectedChapter = chapter;

        const headings = chapter?.themas?.map(t => t.headingText) ?? [];

        this.selectedTopicHeadings = [...headings];
        this._originalSelectedHeadings = [...headings];
    }

    public update(): void {

        if (!this.selectedChapter) {
            return;
        }

        this.requestInProcess = true;

        const chapterId = this.selectedChapter.id;

        this._subjectCategoriesClient.getTopics().pipe(
            first(),
            switchMap(topics => this._projectStateService.firstSwitchMap(project =>
                this._chaptersClient.replaceSubjectCategories(
                    project.id,
                    chapterId,
                    new ApiReplaceSubjectCategoryRequest({
                        subjectCategoryType: SubjectCategoryType.Topic,
                        subjectCategories: this._buildSubjectCategories(topics)
                    })))),
            takeUntil(this._destroy$))
            .subscribe(() => {
                this.requestInProcess = false;
                this._originalSelectedHeadings = [...this.selectedTopicHeadings];
                this._refreshTable$.next();
            });
    }

    private _buildSubjectCategories(topics: ApiTopic[]): ApiSubjectCategory[] {
        return this.selectedTopicHeadings
            .map(heading => topics.find(t => t.heading === heading))
            .filter((t): t is ApiTopic => !!t)
            .map(topic => new ApiSubjectCategory({
                subjectCategoryType: SubjectCategoryType.Topic,
                headingText: topic.heading
            }));
    }

    private _initTable(): TableSettings<ApiChapterSubjectCategoryListItem> {
        return TableSettings.create(ApiChapterSubjectCategoryListItem, {
            dataSource: oDataQuery => this._projectStateService.switchMap(project =>
                this._chaptersClient.getSubjectCategoryList(project.id, SubjectCategoryType.Topic, oDataQuery)),
            columnResizeMode: 'fit',
            paginator: false,
            selectionMode: this.isReadonly ? undefined : 'single',
            emptyMessageText: $localize`No chapters`,
            reload$: this._refreshTable$.asObservable(),
            columns: {
                pos: {
                    width: '100px',
                    centered: true
                },
                themas: {
                    header: $localize`Topics`,
                    displayExpr: item => item.headingText,
                    filterExpr: item => item.headingText,
                    renderAsCommaSeparatedList: true
                }
            },
            hasFilters: false,
            defaultSort: {
                field: x => x.pos
            }
        });
    }
}
